import React, { useState, useEffect } from 'react';
import SMSLayout from '../components/SMSLayout';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Clock,
  Calendar,
  RefreshCw,
  Send,
  Pencil,
  Trash2,
  Pause,
  Play,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Users,
  MoreVertical,
  Eye,
  AlertTriangle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * SMSQueue - File d'attente SMS
 * Gère les campagnes différées et récurrentes
 */
const SMSQueue = () => {
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  
  const [activeTab, setActiveTab] = useState('scheduled');
  const [scheduledCampaigns, setScheduledCampaigns] = useState([]);
  const [recurringCampaigns, setRecurringCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  
  // Edit modal state
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchQueuedCampaigns();
  }, []);

  const fetchQueuedCampaigns = async () => {
    setLoading(true);
    try {
      const [scheduledRes, recurringRes] = await Promise.all([
        axios.get(`${API_URL}/sms-mailing/queue/scheduled`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/sms-mailing/queue/recurring`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setScheduledCampaigns(scheduledRes.data || []);
      setRecurringCampaigns(recurringRes.data || []);
    } catch (error) {
      console.error('Error fetching queue:', error);
      // Initialize with empty arrays on error
      setScheduledCampaigns([]);
      setRecurringCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendNow = async (campaign) => {
    try {
      await axios.post(`${API_URL}/sms-mailing/queue/${campaign.id}/send-now`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Campagne envoyée avec succès !');
      fetchQueuedCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
  };

  const handlePauseResume = async (campaign) => {
    try {
      const action = campaign.status === 'paused' ? 'resume' : 'pause';
      await axios.post(`${API_URL}/sms-mailing/queue/${campaign.id}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(campaign.status === 'paused' ? 'Récurrence reprise' : 'Récurrence mise en pause');
      fetchQueuedCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setEditForm({
      name: campaign.name,
      message: campaign.message,
      scheduled_date: campaign.scheduled_at?.split('T')[0] || '',
      scheduled_time: campaign.scheduled_at?.split('T')[1]?.substring(0, 5) || '',
      recurring_pattern: campaign.recurring?.pattern || 'daily',
      recurring_end_type: campaign.recurring?.end_type || 'never',
      recurring_end_date: campaign.recurring?.end_date || '',
      recurring_occurrences: campaign.recurring?.occurrences || 10
    });
  };

  const handleSaveEdit = async () => {
    if (!editingCampaign) return;
    
    setSaving(true);
    try {
      const payload = {
        name: editForm.name,
        message: editForm.message,
      };
      
      if (editingCampaign.send_mode === 'scheduled') {
        payload.scheduled_at = `${editForm.scheduled_date}T${editForm.scheduled_time}`;
      } else if (editingCampaign.send_mode === 'recurring') {
        payload.recurring = {
          pattern: editForm.recurring_pattern,
          end_type: editForm.recurring_end_type,
          end_date: editForm.recurring_end_type === 'until' ? editForm.recurring_end_date : null,
          occurrences: editForm.recurring_end_type === 'count' ? editForm.recurring_occurrences : null
        };
      }
      
      await axios.put(`${API_URL}/sms-mailing/queue/${editingCampaign.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Campagne modifiée avec succès');
      setEditingCampaign(null);
      fetchQueuedCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/sms-mailing/queue/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Campagne supprimée');
      setDeleteTarget(null);
      fetchQueuedCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRecurringLabel = (pattern) => {
    const labels = {
      daily: 'Quotidien',
      weekly: 'Hebdomadaire',
      monthly: 'Mensuel',
      yearly: 'Annuel'
    };
    return labels[pattern] || pattern;
  };

  const CampaignCard = ({ campaign, isRecurring = false }) => {
    const isExpanded = expandedId === campaign.id;
    
    return (
      <Card className={`${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''} transition-all`}>
        <CardContent className="p-4">
          {/* Header Row */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className={`p-2 rounded-lg ${
                isRecurring 
                  ? isDarkMode ? 'bg-purple-500/20' : 'bg-purple-100'
                  : isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'
              }`}>
                {isRecurring ? (
                  <RefreshCw className={`h-5 w-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                ) : (
                  <Calendar className={`h-5 w-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className={`font-semibold truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {campaign.name}
                  </h3>
                  {campaign.status === 'paused' && (
                    <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                      En pause
                    </Badge>
                  )}
                </div>
                
                <div className={`flex items-center gap-4 mt-1 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {isRecurring ? getRecurringLabel(campaign.recurring?.pattern) : formatDate(campaign.scheduled_at)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" />
                    {campaign.recipients_count || 0} destinataires
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageSquare className="h-3.5 w-3.5" />
                    {campaign.sms_count || 1} SMS/msg
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {!isRecurring && (
                <Button
                  size="sm"
                  onClick={() => handleSendNow(campaign)}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  <Send className="h-4 w-4 mr-1" />
                  Envoyer
                </Button>
              )}
              
              {isRecurring && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handlePauseResume(campaign)}
                  className={campaign.status === 'paused' ? 'text-emerald-600 border-emerald-300' : 'text-amber-600 border-amber-300'}
                >
                  {campaign.status === 'paused' ? (
                    <>
                      <Play className="h-4 w-4 mr-1" />
                      Reprendre
                    </>
                  ) : (
                    <>
                      <Pause className="h-4 w-4 mr-1" />
                      Pause
                    </>
                  )}
                </Button>
              )}
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setExpandedId(isExpanded ? null : campaign.id)}>
                    <Eye className="h-4 w-4 mr-2" />
                    {isExpanded ? 'Masquer détails' : 'Voir détails'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleEdit(campaign)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    Modifier
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => setDeleteTarget(campaign)}
                    className="text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Supprimer
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setExpandedId(isExpanded ? null : campaign.id)}
              >
                {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Expanded Content */}
          {isExpanded && (
            <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-[hsl(220,14%,22%)]' : 'border-slate-200'}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Message Preview */}
                <div>
                  <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Message
                  </h4>
                  <div className={`p-3 rounded-lg text-sm ${isDarkMode ? 'bg-[hsl(220,14%,18%)] text-slate-300' : 'bg-slate-100 text-slate-700'}`}>
                    {campaign.message}
                  </div>
                </div>

                {/* Schedule Details */}
                <div>
                  <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Programmation
                  </h4>
                  <div className={`space-y-2 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                    {isRecurring ? (
                      <>
                        <p><strong>Fréquence :</strong> {getRecurringLabel(campaign.recurring?.pattern)}</p>
                        <p><strong>Prochain envoi :</strong> {formatDate(campaign.next_run)}</p>
                        <p><strong>Fin :</strong> {
                          campaign.recurring?.end_type === 'never' ? 'Sans limite' :
                          campaign.recurring?.end_type === 'until' ? `Jusqu'au ${formatDate(campaign.recurring?.end_date)}` :
                          `${campaign.recurring?.occurrences} occurrences`
                        }</p>
                        <p><strong>Envois effectués :</strong> {campaign.executions_count || 0}</p>
                      </>
                    ) : (
                      <>
                        <p><strong>Date d'envoi :</strong> {formatDate(campaign.scheduled_at)}</p>
                        <p><strong>Créée le :</strong> {formatDate(campaign.created_at)}</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <SMSLayout>
      <div className="space-y-6" data-testid="sms-queue-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              File d'attente
            </h1>
            <p className={`mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Gérez vos campagnes programmées et récurrentes
            </p>
          </div>
          
          <Button onClick={fetchQueuedCampaigns} variant="outline" size="sm">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="scheduled" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Différés
              {scheduledCampaigns.length > 0 && (
                <Badge variant="secondary" className="ml-1">{scheduledCampaigns.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="recurring" className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Récurrents
              {recurringCampaigns.length > 0 && (
                <Badge variant="secondary" className="ml-1">{recurringCampaigns.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="scheduled" className="mt-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
              </div>
            ) : scheduledCampaigns.length === 0 ? (
              <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
                <CardContent className="py-12 text-center">
                  <Calendar className={`h-12 w-12 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`} />
                  <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Aucune campagne différée
                  </h3>
                  <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
                    Les campagnes programmées pour un envoi ultérieur apparaîtront ici
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {scheduledCampaigns.map((campaign) => (
                  <CampaignCard key={campaign.id} campaign={campaign} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="recurring" className="mt-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
              </div>
            ) : recurringCampaigns.length === 0 ? (
              <Card className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
                <CardContent className="py-12 text-center">
                  <RefreshCw className={`h-12 w-12 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-slate-300'}`} />
                  <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    Aucun envoi récurrent
                  </h3>
                  <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
                    Les campagnes récurrentes configurées apparaîtront ici
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {recurringCampaigns.map((campaign) => (
                  <CampaignCard key={campaign.id} campaign={campaign} isRecurring />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Edit Modal */}
        {editingCampaign && (
          <AlertDialog open={!!editingCampaign} onOpenChange={() => setEditingCampaign(null)}>
            <AlertDialogContent className={`max-w-2xl ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
              <AlertDialogHeader>
                <AlertDialogTitle className={isDarkMode ? 'text-white' : ''}>
                  Modifier la campagne
                </AlertDialogTitle>
                <AlertDialogDescription className={isDarkMode ? 'text-slate-400' : ''}>
                  Modifiez les paramètres de la campagne "{editingCampaign.name}"
                </AlertDialogDescription>
              </AlertDialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Campaign Name */}
                <div className="space-y-2">
                  <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : ''}`}>
                    Nom de la campagne
                  </label>
                  <Input
                    value={editForm.name || ''}
                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                    className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}
                  />
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : ''}`}>
                    Message
                  </label>
                  <textarea
                    value={editForm.message || ''}
                    onChange={(e) => setEditForm({...editForm, message: e.target.value})}
                    rows={4}
                    className={`w-full p-3 rounded-lg border resize-none ${
                      isDarkMode 
                        ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' 
                        : 'border-slate-200'
                    }`}
                  />
                </div>

                {/* Schedule Settings */}
                {editingCampaign.send_mode === 'scheduled' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : ''}`}>
                        Date
                      </label>
                      <Input
                        type="date"
                        value={editForm.scheduled_date || ''}
                        onChange={(e) => setEditForm({...editForm, scheduled_date: e.target.value})}
                        className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : ''}`}>
                        Heure
                      </label>
                      <Input
                        type="time"
                        value={editForm.scheduled_time || ''}
                        onChange={(e) => setEditForm({...editForm, scheduled_time: e.target.value})}
                        className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}
                      />
                    </div>
                  </div>
                )}

                {/* Recurring Settings */}
                {editingCampaign.send_mode === 'recurring' && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : ''}`}>
                        Fréquence
                      </label>
                      <Select 
                        value={editForm.recurring_pattern} 
                        onValueChange={(v) => setEditForm({...editForm, recurring_pattern: v})}
                      >
                        <SelectTrigger className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Quotidien</SelectItem>
                          <SelectItem value="weekly">Hebdomadaire</SelectItem>
                          <SelectItem value="monthly">Mensuel</SelectItem>
                          <SelectItem value="yearly">Annuel</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : ''}`}>
                        Fin de la récurrence
                      </label>
                      <Select 
                        value={editForm.recurring_end_type} 
                        onValueChange={(v) => setEditForm({...editForm, recurring_end_type: v})}
                      >
                        <SelectTrigger className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="never">Sans limite</SelectItem>
                          <SelectItem value="until">Jusqu'à une date</SelectItem>
                          <SelectItem value="count">Nombre d'occurrences</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {editForm.recurring_end_type === 'until' && (
                      <div className="space-y-2">
                        <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : ''}`}>
                          Date de fin
                        </label>
                        <Input
                          type="date"
                          value={editForm.recurring_end_date || ''}
                          onChange={(e) => setEditForm({...editForm, recurring_end_date: e.target.value})}
                          className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}
                        />
                      </div>
                    )}

                    {editForm.recurring_end_type === 'count' && (
                      <div className="space-y-2">
                        <label className={`text-sm font-medium ${isDarkMode ? 'text-slate-200' : ''}`}>
                          Nombre d'occurrences
                        </label>
                        <Input
                          type="number"
                          min="1"
                          value={editForm.recurring_occurrences || 10}
                          onChange={(e) => setEditForm({...editForm, recurring_occurrences: parseInt(e.target.value)})}
                          className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white' : ''}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <AlertDialogFooter>
                <AlertDialogCancel className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white hover:bg-[hsl(220,14%,22%)]' : ''}>
                  Annuler
                </AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    'Enregistrer'
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
          <AlertDialogContent className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
            <AlertDialogHeader>
              <AlertDialogTitle className={`flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Supprimer la campagne
              </AlertDialogTitle>
              <AlertDialogDescription className={isDarkMode ? 'text-slate-400' : ''}>
                Êtes-vous sûr de vouloir supprimer la campagne "{deleteTarget?.name}" ?
                Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className={isDarkMode ? 'bg-[hsl(220,14%,18%)] border-[hsl(220,14%,25%)] text-white hover:bg-[hsl(220,14%,22%)]' : ''}>
                Annuler
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Suppression...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Supprimer
                  </>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </SMSLayout>
  );
};

export default SMSQueue;
